import { Action } from 'redux'
import { combineEpics, Epic, ofType } from 'redux-observable'
import {
  catchError,
  EMPTY,
  filter,
  from,
  groupBy,
  map,
  merge,
  mergeMap,
  NEVER,
  of,
  switchMap,
  takeUntil
} from 'rxjs'
import { getHub } from '../../helpers/hub'
import { getDepthOfMarketOrder } from '../depthOfMarket/selectors'
import { rejectOrder } from '../lastLook/actions'
import { cancelOrder, createTempAggressorOrder } from '../order/actions'
import {
  getAggressorOrderByInitialOrder,
  getUserOrOperatorOrderById
} from '../order/selectors'
import { logError } from '../ws/actions'
import {
  addAggressorWindow,
  AggressorWindowClosedAction,
  FetchDataForSecurityModalAction,
  foregroundAggressorWindow,
  LastLookWindowClosedAction,
  OpenAggressorWindowAction,
  removeAggressorWindow,
  UnsubscribeFetchDataForSecurityModalAction,
  updateDataForSecurityModal
} from './actions'
import { createSecurityModalInfoFromResponse } from './helpers'
import { getAggressorWindows } from './selectors'
import { SecurityModalInfo } from './types'

const fetchDataForSecurityModalEpic: Epic = (action$, state$) =>
  action$.pipe(
    ofType<FetchDataForSecurityModalAction>(
      'windows.fetchDataForSecurityModal'
    ),
    /* Key by orderId so an early warm-up fetch and the modal's own fetch collapse
       to one GetAggressorView stream — switchMap hands off to the latest. The
       duration selector completes the group when its order unsubscribes, so
       groupBy stops retaining a GroupedObservable per orderId forever (an
       otherwise unbounded leak on the infinite action stream). */
    groupBy(
      (action) => action.payload.orderId,
      undefined,
      (group$) =>
        merge(
          action$.pipe(
            ofType<UnsubscribeFetchDataForSecurityModalAction>(
              'windows.unsubscribeFetchDataForSecurityModal'
            ),
            filter(
              (unsubscribeAction) =>
                unsubscribeAction.payload.orderId === group$.key
            )
          ),
          NEVER
        )
    ),
    mergeMap((group$) =>
      group$.pipe(
        switchMap(({ payload: { orderId, size } }) => {
          const selUser = state$.value.users
            ? state$.value.users.selectedUser
            : 0
          return getHub()
            .stream<SecurityModalInfo>(
              'GetAggressorView',
              selUser,
              orderId,
              size
            )
            .pipe(
              map(createSecurityModalInfoFromResponse),
              map((securityModalInfo) =>
                updateDataForSecurityModal(orderId, securityModalInfo)
              ),
              takeUntil(
                action$.pipe(
                  ofType<UnsubscribeFetchDataForSecurityModalAction>(
                    'windows.unsubscribeFetchDataForSecurityModal'
                  ),
                  filter(
                    (unsubscribeAction) =>
                      unsubscribeAction.payload.orderId === orderId
                  )
                )
              ),
              catchError((err) => of(logError(err)))
            )
        })
      )
    )
  )

const openAggressorWindowEpic: Epic<Action> = (action$, state$) =>
  action$.pipe(
    ofType<OpenAggressorWindowAction>('windows.openAggressorWindow'),
    mergeMap((action) => {
      const { initialOrderId, securityId, listId } = action.payload
      const windows = getAggressorWindows(state$.value)
      const initialOrder = getDepthOfMarketOrder(state$.value)(
        initialOrderId,
        securityId
      )
      const getAggressorOrder = getAggressorOrderByInitialOrder(state$.value)
      if (!initialOrder) {
        /* The depth order is consumed once the trade completes, and
           OpenFinAggressor re-dispatches open on every depth tick, so a missing
           order is expected for an already-open window. Never throw here — an
           uncaught epic error tears down the whole stream and freezes the popout. */
        return EMPTY
      }

      const existingWindow = windows.find(
        (window) => window.initialOrderId === initialOrderId
      )
      if (existingWindow) {
        const aggressorOrder = getAggressorOrder(initialOrderId)
        /* Re-opening while an attempt is still in flight must not spawn a fresh
           temp order — that re-points the window and reverts the footer to
           creationPending. Absent or in-flight just foregrounds the existing
           window; only a terminal prior attempt (e.g. cancelled) re-aggresses below. */
        const inFlight =
          !aggressorOrder ||
          aggressorOrder.status === 'creationPending' ||
          aggressorOrder.status === 'pending' ||
          aggressorOrder.status === 'waitingForConfirmation'
        if (inFlight) {
          return of(foregroundAggressorWindow(initialOrderId))
        }
      }

      const transactionId = Math.random()
      return from([
        createTempAggressorOrder(initialOrder, transactionId),
        ...(existingWindow ? [removeAggressorWindow(existingWindow)] : []),
        addAggressorWindow({
          initialOrderId: action.payload.initialOrderId,
          securityId,
          transactionId,
          listId
        })
      ])
    })
  )

/* The user closed an Aggressor window (taskbar / Alt+F4 / in-app Close
   button) while an aggress attempt was still awaiting the counterparty.
   Cancel the attempt */
const aggressorWindowClosedEpic: Epic<Action> = (action$, state$) =>
  action$.pipe(
    ofType<AggressorWindowClosedAction>('windows.aggressorWindowClosed'),
    mergeMap((action) => {
      const order = getAggressorOrderByInitialOrder(state$.value)(
        action.payload.initialOrderId
      )
      if (
        order?.id &&
        (order.status === 'pending' ||
          order.status === 'waitingForConfirmation')
      ) {
        return of(cancelOrder(order.id))
      }
      return EMPTY
    })
  )

// The user closed a Last Look window without deciding — reject the order.
const lastLookWindowClosedEpic: Epic<Action> = (action$, state$) =>
  action$.pipe(
    ofType<LastLookWindowClosedAction>('windows.lastLookWindowClosed'),
    mergeMap((action) => {
      const { orderId } = action.payload
      const order = getUserOrOperatorOrderById(state$.value)(orderId)
      const status = order?.aggressorOrder?.status
      if (status === 'pending' || status === 'waitingForConfirmation') {
        return of(rejectOrder(orderId))
      }
      return EMPTY
    })
  )

export default combineEpics(
  openAggressorWindowEpic,
  fetchDataForSecurityModalEpic,
  aggressorWindowClosedEpic,
  lastLookWindowClosedEpic
)
